import {
  AudioLines,
  BarChart3,
  Bell,
  Calculator,
  CalendarDays,
  ClipboardList,
  Columns3,
  CreditCard,
  FileText,
  FolderKanban,
  History,
  Landmark,
  LayoutDashboard,
  ListTodo,
  Package,
  Palette,
  Receipt,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Ship,
  Users,
  Wallet,
} from "lucide-react";

/** Display metadata for a shell navigation destination. */
export interface NavigationItem {
  description: string;
  icon: typeof LayoutDashboard;
  label: string;
  path: string;
}

/** Group of related shell navigation destinations. */
export interface NavigationGroup {
  description: string;
  items: NavigationItem[];
  label: string;
}

const financeItems: NavigationItem[] = [
  {
    label: "Finance Overview",
    description: "Cash flow, aging, and budget health",
    icon: Landmark,
    path: "/finance",
  },
  {
    label: "Dashboard",
    description: "Executive snapshot and daily priorities",
    icon: LayoutDashboard,
    path: "/",
  },
  {
    label: "Accounts",
    description: "Customers, vendors, and ledgers",
    icon: Users,
    path: "/accounts",
  },
  {
    label: "Purchase Orders",
    description: "Supplier commitments and spend",
    icon: Receipt,
    path: "/purchase-orders",
  },
  {
    label: "PO Kanban",
    description: "Transfer products between purchase orders",
    icon: Columns3,
    path: "/purchase-orders/kanban",
  },
  {
    label: "Invoices",
    description: "Revenue, billing, and collections",
    icon: FileText,
    path: "/invoices",
  },
  {
    label: "Estimates",
    description: "Pipeline, quoting, and conversion",
    icon: ClipboardList,
    path: "/estimates",
  },
  {
    label: "Analytics",
    description: "KPI trends and financial signals",
    icon: BarChart3,
    path: "/analytics",
  },
  {
    label: "Accounting",
    description: "Close books and monitor balances",
    icon: Calculator,
    path: "/accounting",
  },
  {
    label: "Portal Payments",
    description: "External payment operations",
    icon: ShieldCheck,
    path: "/portal-payments",
  },
  {
    label: "Customer Payments",
    description: "Incoming payment reconciliation",
    icon: CreditCard,
    path: "/customer-payments",
  },
  {
    label: "Vendor Payments",
    description: "Outgoing disbursements and timing",
    icon: Receipt,
    path: "/vendor-payments",
  },
  {
    label: "Credits",
    description: "Customer credit balances and use",
    icon: CreditCard,
    path: "/credits",
  },
  {
    label: "Vendor Credits",
    description: "Supplier credits and offsets",
    icon: Wallet,
    path: "/vendor-credits",
  },
];

const operationsItems: NavigationItem[] = [
  {
    label: "Expenses",
    description: "Operating spend and approvals",
    icon: Receipt,
    path: "/expenses",
  },
  {
    label: "Inventory",
    description: "Stock levels, value, and movement",
    icon: Package,
    path: "/inventory",
  },
  {
    label: "Inventory Management",
    description: "Analytics, alerts, and vendor performance",
    icon: BarChart3,
    path: "/inventory-management",
  },
  {
    label: "Operations",
    description: "Logistics and operational workflows",
    icon: Ship,
    path: "/operations",
  },
  {
    label: "Theme Showcase",
    description: "Design tokens and component gallery",
    icon: Palette,
    path: "/theme",
  },
  {
    label: "Sync",
    description: "Integration health and cleanup",
    icon: RefreshCw,
    path: "/sync",
  },
  {
    label: "Settings",
    description: "Workspace, appearance, and display preferences",
    icon: Settings,
    path: "/settings",
  },
];

const managementItems: NavigationItem[] = [
  {
    label: "Projects",
    description: "Delivery pipelines and ownership",
    icon: FolderKanban,
    path: "/projects",
  },
  {
    label: "Tasks",
    description: "Work queue and day-to-day execution",
    icon: ListTodo,
    path: "/tasks",
  },
  {
    label: "Task Search",
    description: "Find work by scope, owner, or status",
    icon: Search,
    path: "/tasks/search",
  },
  {
    label: "Task Calendar",
    description: "Timeline view of due work",
    icon: CalendarDays,
    path: "/tasks/calendar",
  },
  {
    label: "Task Activity",
    description: "Recent updates and delivery cadence",
    icon: History,
    path: "/tasks/activity",
  },
  {
    label: "Notifications",
    description: "Alerts, approvals, and account activity",
    icon: Bell,
    path: "/notifications",
  },
  {
    label: "Voice Assistant",
    description: "Saved text and voice threads",
    icon: AudioLines,
    path: "/assistant",
  },
];

/** Primary navigation groups rendered by the app shell. */
export const navigationGroups: NavigationGroup[] = [
  {
    label: "Finance",
    description: "Revenue, payables, and accounting controls",
    items: financeItems,
  },
  {
    label: "Operations",
    description: "Inventory, spend, and system hygiene",
    items: operationsItems,
  },
  {
    label: "Management",
    description: "Projects, tasks, and execution planning",
    items: managementItems,
  },
];

/** Frequently used destinations shown as quick actions in the shell. */
export const shellQuickLinks = [
  { label: "Draft invoice", path: "/invoices/create" },
  { label: "Review PO board", path: "/purchase-orders/kanban" },
  { label: "Open task queue", path: "/tasks" },
];

/** Centralizes active-state policy so shell navigation surfaces stay consistent for nested routes. */
export function isNavigationItemActive(pathname: string, itemPath: string) {
  return itemPath === "/"
    ? pathname === "/"
    : pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

const navigationItems: NavigationItem[] = navigationGroups.flatMap((group) => group.items);

function titleFromPathname(pathname: string): string {
  const [segment] = pathname.split("/").filter(Boolean);
  if (!segment) {
    return "Dashboard";
  }
  return segment
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Resolves the most specific navigation item for the active route. Nested
 * routes leave a parent item active too, but only this one is the current page.
 */
export function resolveCurrentNavigationItem(pathname: string): NavigationItem | undefined {
  return navigationItems
    .filter((item) => isNavigationItemActive(pathname, item.path))
    .sort((a, b) => b.path.length - a.path.length)
    .at(0);
}

/** Resolves the most specific navigation label for the active route. */
export function resolvePageTitle(pathname: string): string {
  return resolveCurrentNavigationItem(pathname)?.label ?? titleFromPathname(pathname);
}
